import {
	Conversation,
	ConversationContent,
	ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	CableIcon,
	CreditCardIcon,
	GlobeIcon,
	LayoutGridIcon,
	ListTodoIcon,
	LogOutIcon,
	MegaphoneIcon,
	MessagesSquareIcon,
	MonitorIcon,
	MoonIcon,
	PanelLeftIcon,
	SearchIcon,
	SettingsIcon,
	SquarePenIcon,
	SunIcon,
	UsersIcon,
	type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNarrowViewport } from "./shell.js";
import { MairkoMark } from "./MairkoMark.js";
import { AgentsHub } from "./AgentsHub.js";
import { cn } from "./cn.js";
import {
	ChannelsHub,
	ConnectorsHub,
	type ChannelsFilter,
	type ChannelsView,
	type ConnectorsFilter,
	type ConnectorsView,
} from "./ConnectorsHub.js";
import { SitesHub, WebsiteDeskStrip } from "./SitesHub.js";
import { SitesNavButton } from "./SitesNavButton.js";
import { useSiteClonePoll } from "./CloneSiteCard.js";
import { useSiteDesignPoll } from "./DesignSiteCard.js";
import type { PlatformConnection } from "./connections-api.js";
import { DeleteChatConfirm } from "./DeleteChatConfirm.js";
import {
	readStoredModel,
	writeStoredModel,
	type ChatModelId,
} from "./chat-model.js";
import { DevModelSelect, DevResetTrigger, useDevReset } from "./DevResetButton.js";
import { IntegrationDetail } from "./IntegrationDetail.js";
import { Message } from "./Message.js";
import { MairkoComposer } from "./MairkoComposer.js";
import {
	brandFromThread,
	chatHasBackgroundWork,
	chatMessagesBusy,
	collectSiteClones,
	collectSiteDesigns,
	isOrphanStatusFiller,
	withHeldAfterSiteClone,
	withLatestSiteCloneCards,
	siteCloneInFlight,
	withWorkingCopyAsk,
	type CardAction,
	type ChatCompanyResearch,
	type ChatHandoff,
	type ChatMessage,
	type ChatSiteClone,
	type ChatSiteDesign,
	type ChatWorkingCopyAsk,
	type HandoffDecisionEvent,
	type PublishDecisionEvent,
	type SiteCloneCardEvent,
} from "./messages.js";
import {
	createMairkoThread,
	sortMairkoThreads,
	type MairkoThread,
} from "./mairko-threads.js";
import type { AgentId, Integration } from "./nav.js";
import { RecentThreadRow } from "./RecentThreadRow.js";
import { hostFromUrl } from "./SiteFavicon.js";
import { SitePreview } from "./SitePreview.js";
import { PlanBadge } from "./PlanBadge.js";
import type { ThemePreference } from "./theme.js";

export type ChatSurface =
	| "messages"
	| "agents"
	| "connectors"
	| "channels"
	| "sites";

function ThemeToggleIcon({ preference }: { preference: ThemePreference }) {
	switch (preference) {
		case "light":
			return <SunIcon className="size-4" />;
		case "dark":
			return <MoonIcon className="size-4" />;
		case "system":
			return <MonitorIcon className="size-4" />;
		default: {
			const _exhaustive: never = preference;
			return _exhaustive;
		}
	}
}

/** Instant label on icon chrome — replaces slow native `title` tooltips. */
function SideTip({
	label,
	side = "right",
	children,
}: {
	label: string;
	side?: "right" | "top";
	children: ReactElement;
}) {
	return (
		<Tooltip delayDuration={200}>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side={side} sideOffset={10}>
				{label}
			</TooltipContent>
		</Tooltip>
	);
}

function isFreshThread(messages: ChatMessage[]): boolean {
	const human = messages.some(
		(m) => m.author === "human" && m.body.trim().length > 0,
	);
	return !human;
}

function estimateTokens(messages: ChatMessage[]): number {
	let chars = 0;
	for (const row of messages) {
		chars += row.body.length + (row.reasoning?.length ?? 0);
	}
	return Math.min(200_000, Math.round(chars / 4));
}

export function previewUrlFromHandoffs(
	handoffs: Array<{ previewUrl?: string; summary?: string }> | undefined,
): string | null {
	if (!handoffs) return null;
	for (const handoff of [...handoffs].reverse()) {
		if (isSitePreviewUrl(handoff.previewUrl)) return handoff.previewUrl!;
		const fromSummary = handoff.summary?.match(/siteUrl=(https?:\/\/\S+)/i)?.[1];
		if (fromSummary) return fromSummary;
	}
	return null;
}

/** Hosted-site preview paths are relative (`/api/sites/…/preview/…`). */
export function isSitePreviewUrl(url: string | null | undefined): url is string {
	if (!url) return false;
	if (url.startsWith("http://") || url.startsWith("https://")) return true;
	return url.startsWith("/api/sites/") && url.includes("/preview");
}

export function previewUrlFromSiteClones(
	clones: Array<{ previewUrl?: string | null }> | undefined,
): string | null {
	if (!clones) return null;
	for (const clone of [...clones].reverse()) {
		if (isSitePreviewUrl(clone.previewUrl)) return clone.previewUrl;
	}
	return null;
}

type Props = {
	userName: string;
	firstName?: string;
	userImageUrl?: string | null;
	threads: MairkoThread[];
	activeThreadId: string;
	draft: string;
	typing: boolean;
	onDraftChange: (value: string) => void;
	onSubmit: (
		text: string,
		meta?: {
			modelId?: ChatModelId;
			plugins?: string[];
			files?: Array<{
				mediaType?: string;
				url?: string;
				filename?: string;
			}>;
		},
	) => void;
	onEditMessage: (messageId: string, body: string) => void;
	onRetryMessage: (messageId: string) => void;
	onSelectThread: (id: string) => void;
	onNewChat: () => void;
	onPinThread: (id: string) => void;
	onRenameThread: (id: string, title: string) => void;
	onDeleteThread: (id: string) => void;
	onOpenDesk: () => void;
	onOpenAgent: (id: AgentId) => void;
	onOpenSearch: () => void;
	onOpenSettings: () => void;
	onOpenBilling: () => void;
	onSignOut: () => void;
	themePref: ThemePreference;
	onCycleTheme: () => void;
	/** Same Agents / Connectors / Channels hubs as Desk — stay in the chat shell. */
	surface: ChatSurface;
	onSurfaceChange: (surface: ChatSurface) => void;
	setupDone: boolean;
	connectors: readonly Integration[];
	connectorsView: ConnectorsView;
	connectorsFilter: ConnectorsFilter;
	onConnectorsViewChange: (view: ConnectorsView) => void;
	onConnectorsFilterChange: (filter: ConnectorsFilter) => void;
	channels: readonly Integration[];
	channelsView: ChannelsView;
	channelsFilter: ChannelsFilter;
	onChannelsViewChange: (view: ChannelsView) => void;
	onChannelsFilterChange: (filter: ChannelsFilter) => void;
	selectedIntegration: Integration | null;
	onSelectIntegration: (
		item: Integration,
		kind: "connector" | "channel",
	) => void;
	onClearIntegration: () => void;
	platformRowFor: (matrixId: string) => PlatformConnection | null;
	onConnectionChange: () => Promise<void> | void;
	oauthFlash?: string | null;
	oauthFlashTone?: "ok" | "err";
	/** Live site canvas (WordPress / Sites) opened from Rhea handoffs. */
	sitePreviewUrl?: string | null;
	onCloseSitePreview?: () => void;
	onOpenSitePreview?: (url: string) => void;
	onHandoffPatch?: (handoffId: string, patch: Partial<ChatHandoff>) => void;
	onHandoffDecision?: (event: HandoffDecisionEvent) => void;
	onSiteClonePatch?: (siteId: string, patch: Partial<ChatSiteClone>) => void;
	onSiteDesignPatch?: (siteId: string, patch: Partial<ChatSiteDesign>) => void;
	onPublishSite?: (siteId: string, siteName: string) => void;
	onPublishDecision?: (event: PublishDecisionEvent) => void;
	onSiteCloneCard?: (event: SiteCloneCardEvent) => void;
	onCompanyResearchPatch?: (
		packId: string,
		patch: Partial<ChatCompanyResearch>,
	) => void;
	onConfirmCompanyPack?: (packId: string) => void;
	confirmingPackId?: string | null;
	onWorkingCopyYes?: (ask: ChatWorkingCopyAsk, messageId: string) => void;
	workingCopyCheckout?: {
		messageId: string;
		ask: ChatWorkingCopyAsk;
	} | null;
	onWorkingCopyCheckoutChange?: (
		next: { messageId: string; ask: ChatWorkingCopyAsk } | null,
	) => void;
	onWorkingCopyPaid?: () => void;
	onWorkingCopyLater?: (messageId: string) => void;
	onCardAction?: (action: CardAction) => void;
};

/** ChatGPT / Claude-style master chat — own shell, not the Desk. */
function OnboardingIntro({ firstName }: { firstName?: string }) {
	const hello = firstName?.trim() ? `Hey, ${firstName.trim()}.` : "Hey.";
	return (
		<div className="mc-ob-intro">
			<MairkoMark size="home" shimmer="off" />
			<h1 className="mc-ob-hello">{hello}</h1>
		</div>
	);
}

type HomeStarter = {
	label: string;
	hint: string;
	send: string;
	icon: LucideIcon;
};

const DESK_STARTERS: readonly HomeStarter[] = [
	{
		label: "What’s connected?",
		hint: "Ads, analytics, and the rest.",
		send: "What’s connected right now?",
		icon: CableIcon,
	},
	{
		label: "Check my ads",
		hint: "What’s spending and what it’s doing.",
		send: "Check my ads.",
		icon: MegaphoneIcon,
	},
	{
		label: "How’s the website?",
		hint: "The site we have for you.",
		send: "How’s the website looking?",
		icon: GlobeIcon,
	},
	{
		label: "What should we do?",
		hint: "One next step, not a pile of ideas.",
		send: "What should we work on this week?",
		icon: ListTodoIcon,
	},
];

function HomeStarters({
	items,
	disabled,
	onPick,
}: {
	items: readonly HomeStarter[];
	disabled?: boolean;
	onPick: (send: string) => void;
}) {
	return (
		<ul className="mc-starters" aria-label="Get started">
			{items.map((item) => {
				const Icon = item.icon;
				return (
					<li key={item.label}>
						<button
							type="button"
							className="mc-starter"
							disabled={disabled}
							onClick={() => onPick(item.send)}
						>
							<Icon aria-hidden />
							<span>
								<span className="mc-starter-label">{item.label}</span>
								<span className="mc-starter-hint">{item.hint}</span>
							</span>
						</button>
					</li>
				);
			})}
		</ul>
	);
}

export function MairkoChatView({
	userName,
	firstName,
	userImageUrl,
	threads,
	activeThreadId,
	draft,
	typing,
	onDraftChange,
	onSubmit,
	onEditMessage,
	onRetryMessage,
	onSelectThread,
	onNewChat,
	onPinThread,
	onRenameThread,
	onDeleteThread,
	onOpenDesk,
	onOpenAgent,
	onOpenSearch,
	onOpenSettings,
	onOpenBilling,
	onSignOut,
	themePref,
	onCycleTheme,
	surface,
	onSurfaceChange,
	setupDone,
	connectors,
	connectorsView,
	connectorsFilter,
	onConnectorsViewChange,
	onConnectorsFilterChange,
	channels,
	channelsView,
	channelsFilter,
	onChannelsViewChange,
	onChannelsFilterChange,
	selectedIntegration,
	onSelectIntegration,
	onClearIntegration,
	platformRowFor,
	onConnectionChange,
	oauthFlash = null,
	oauthFlashTone = "ok",
	sitePreviewUrl = null,
	onCloseSitePreview,
	onOpenSitePreview,
	onPublishSite,
	onPublishDecision,
	onSiteCloneCard,
	onHandoffPatch,
	onHandoffDecision,
	onSiteClonePatch,
	onSiteDesignPatch,
	onCompanyResearchPatch,
	onConfirmCompanyPack,
	confirmingPackId,
	onWorkingCopyYes,
	workingCopyCheckout,
	onWorkingCopyPaid,
	onWorkingCopyLater,
	onCardAction,
}: Props) {
	const locked = !setupDone;
	const { openReset, dialog: resetDialog } = useDevReset();
	const [modelId, setModelId] = useState<ChatModelId>(() => readStoredModel());
	const narrow = useNarrowViewport();
	const [sidebarOpen, setSidebarOpen] = useState(() =>
		typeof window !== "undefined"
			? !window.matchMedia("(max-width: 720px)").matches
			: true,
	);
	const [focusNonce, setFocusNonce] = useState(0);
	const [sitesCoach, setSitesCoach] = useState(false);

	useEffect(() => {
		if (locked) {
			setSidebarOpen(false);
			return;
		}
		setSidebarOpen(!narrow);
	}, [locked, narrow]);

	useEffect(() => {
		if (!narrow || !sidebarOpen) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setSidebarOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [narrow, sidebarOpen]);

	const closeDrawerIfNarrow = () => {
		if (narrow) setSidebarOpen(false);
	};
	const railCollapsed = !sidebarOpen && !narrow;
	const [pendingDelete, setPendingDelete] = useState<{
		id: string;
		title: string;
	} | null>(null);
	const active =
		threads.find((t) => t.id === activeThreadId) ??
		threads[0] ??
		createMairkoThread();
	const messages = active.messages;
	const visibleMessages = useMemo(() => {
		const brand = brandFromThread(messages);
		return withHeldAfterSiteClone(
			withLatestSiteCloneCards(
				messages
					.map((message) => withWorkingCopyAsk(message, brand))
					.filter((message) => !isOrphanStatusFiller(message)),
			),
		);
	}, [messages]);
	const hasOnboardingWork = messages.some(
		(row) =>
			row.author === "human" || (row.companyResearch?.length ?? 0) > 0,
	);
	const showOnboardingHome = locked && !hasOnboardingWork;
	const fresh = isFreshThread(messages) && !locked;
	const showAgents = !locked && surface === "agents";
	const showConnectors = !locked && surface === "connectors";
	const showChannels = !locked && surface === "channels";
	const showSites = surface === "sites";
	const showCatalog = showAgents || showConnectors || showChannels || showSites;
	const allSiteClones = useMemo(() => {
		const byId = new Map<string, ChatSiteClone>();
		for (const thread of threads) {
			for (const clone of collectSiteClones(thread.messages)) {
				byId.set(clone.siteId, clone);
			}
		}
		return [...byId.values()];
	}, [threads]);
	useSiteClonePoll(allSiteClones, onSiteClonePatch);
	const allSiteDesigns = useMemo(() => {
		const byId = new Map<string, ChatSiteDesign>();
		for (const thread of threads) {
			for (const row of collectSiteDesigns(thread.messages)) {
				byId.set(row.siteId, row);
			}
		}
		return [...byId.values()];
	}, [threads]);
	useSiteDesignPoll(allSiteDesigns, onSiteDesignPatch);
	const cloning = allSiteClones.some(siteCloneInFlight);
	const showSitesNav = !locked || allSiteClones.length > 0;
	const previewTitle = useMemo(() => {
		if (!sitePreviewUrl) return undefined;
		const clone = allSiteClones.find(
			(row) =>
				row.previewUrl === sitePreviewUrl ||
				sitePreviewUrl.includes(row.siteId),
		);
		if (clone?.sourceUrl) return hostFromUrl(clone.sourceUrl) || clone.name;
		const design = allSiteDesigns.find(
			(row) =>
				row.previewUrl === sitePreviewUrl ||
				sitePreviewUrl.includes(row.siteId),
		);
		if (!design) return undefined;
		return hostFromUrl(design.sourceUrl) || design.name || undefined;
	}, [sitePreviewUrl, allSiteClones, allSiteDesigns]);

	useEffect(() => {
		setSitesCoach(cloning);
	}, [cloning]);

	const openHostedSite = Boolean(
		sitePreviewUrl && isSitePreviewUrl(sitePreviewUrl),
	);
	const websiteDeskHost = previewTitle ?? "This website";

	const openSites = () => {
		setSitesCoach(false);
		onSurfaceChange("sites");
		closeDrawerIfNarrow();
	};
	const showHome = fresh && !showCatalog;
	const catalogTitle = (() => {
		switch (surface) {
			case "agents":
				return "Agents";
			case "channels":
				return "Channels";
			case "connectors":
				return "Connectors";
			case "sites":
				return "Websites";
			case "messages":
				return "";
			default: {
				const _exhaustive: never = surface;
				return _exhaustive;
			}
		}
	})();
	const profileInitial = (userName.trim()[0] ?? "Y").toUpperCase();
	const usedTokens = estimateTokens(messages);

	const sortedRecents = useMemo(
		() => sortMairkoThreads(threads),
		[threads],
	);

	useEffect(() => {
		if (fresh) setFocusNonce((n) => n + 1);
	}, [activeThreadId, fresh]);

	useEffect(() => {
		writeStoredModel(modelId);
	}, [modelId]);

	const goMessages = () => onSurfaceChange("messages");

	useEffect(() => {
		if (locked && surface !== "messages" && surface !== "sites") {
			onSurfaceChange("messages");
		}
	}, [locked, surface, onSurfaceChange]);

	const handleNewChat = () => {
		if (locked) return;
		goMessages();
		onNewChat();
		setFocusNonce((n) => n + 1);
		closeDrawerIfNarrow();
	};

	const handleSelectThread = (id: string) => {
		goMessages();
		onSelectThread(id);
		closeDrawerIfNarrow();
	};

	const openSurface = (next: ChatSurface) => {
		onSurfaceChange(next);
		closeDrawerIfNarrow();
	};

	const composer = (variant: "home" | "dock") => (
		<MairkoComposer
			variant={variant}
			draft={draft}
			typing={typing}
			focusKey={`${activeThreadId}-${focusNonce}-${variant}`}
			autoFocus={variant === "home" || fresh}
			usedTokens={usedTokens}
			modelId={modelId}
			onDraftChange={onDraftChange}
			onSubmit={(text, meta) => onSubmit(text, meta)}
		/>
	);

	return (
		<div
			className={cn(
				"mc-shell",
				locked && "mc-shell--onboarding",
				sidebarOpen
					? "mc-shell--sidebar-open"
					: "mc-shell--sidebar-closed",
				(showHome || showOnboardingHome) && "mc-shell--home",
			)}
		>
			<aside
				className={cn(
					"mc-sidebar",
					railCollapsed && "mc-sidebar--collapsed",
				)}
				aria-label="Chats"
				aria-expanded={sidebarOpen}
				aria-hidden={narrow && !sidebarOpen}
				inert={narrow && !sidebarOpen ? true : undefined}
				onClick={
					railCollapsed
						? (event) => {
								const target = event.target;
								if (!(target instanceof Element)) return;
								// Let real controls keep their action; empty chrome expands.
								if (
									target.closest(
										"button, a, input, textarea, [role='menuitem'], [data-radix-collection-item]",
									)
								) {
									return;
								}
								setSidebarOpen(true);
							}
						: undefined
				}
			>
				<div className="mc-sidebar-top">
					<div className="mc-brand-row">
						{sidebarOpen || narrow ? (
							<>
								<img
									className="brand-logo brand-logo--dark"
									src="/brand/mairko-logo-primary.svg"
									alt="Mairko"
									height={22}
								/>
								<img
									className="brand-logo brand-logo--light"
									src="/brand/mairko-logo-onlight.svg"
									alt="Mairko"
									height={22}
								/>
								<SideTip label="Close sidebar">
									<Button
										type="button"
										variant="ghost"
										size="icon-sm"
										className="mc-sidebar-close ml-auto"
										aria-label="Close sidebar"
										onClick={() => setSidebarOpen(false)}
									>
										<PanelLeftIcon className="size-4" />
									</Button>
								</SideTip>
							</>
						) : (
							<SideTip label="Open sidebar">
								<button
									type="button"
									className="mc-brand-icon"
									aria-label="Open sidebar"
									onClick={() => setSidebarOpen(true)}
								>
									<img
										src="/brand/mairko-icon.svg"
										alt=""
										width={22}
										height={22}
										draggable={false}
									/>
								</button>
							</SideTip>
						)}
					</div>

					{locked ? (
						<>
							<p className="mc-side-setup">
								<span className="mc-side-text">Chat</span>
							</p>
							{showSitesNav ? (
								<SitesNavButton
									active={showSites}
									clones={allSiteClones}
									coach={sitesCoach}
									onOpen={openSites}
								/>
							) : null}
						</>
					) : (
						<>
					<SideTip label="New chat">
						<Button
							type="button"
							className="mc-new-chat"
							onClick={handleNewChat}
							aria-label="New chat"
						>
							<SquarePenIcon className="size-4 shrink-0" />
							<span className="mc-side-text">New chat</span>
						</Button>
					</SideTip>

					<SideTip label="Search chats">
						<button
							type="button"
							className="mc-side-search"
							onClick={onOpenSearch}
							aria-label="Search chats"
						>
							<SearchIcon className="size-3.5 shrink-0" />
							<span className="mc-side-text">Search chats</span>
						</button>
					</SideTip>

					<SideTip label="Open Desk">
						<button
							type="button"
							className="mc-side-nav-item mc-side-desk"
							onClick={() => {
								onOpenDesk();
								closeDrawerIfNarrow();
							}}
							aria-label="Open Desk"
						>
							<LayoutGridIcon className="size-4 shrink-0" />
							<span className="mc-side-text">Open Desk</span>
						</button>
					</SideTip>

					<SideTip label="Agents">
						<button
							type="button"
							className="mc-side-nav-item"
							aria-current={showAgents ? "page" : undefined}
							aria-label="Agents"
							onClick={() => openSurface("agents")}
						>
							<UsersIcon className="size-4 shrink-0" />
							<span className="mc-side-text">Agents</span>
						</button>
					</SideTip>

					<SitesNavButton
						active={showSites}
						clones={allSiteClones}
						coach={sitesCoach}
						onOpen={openSites}
					/>

					<SideTip label="Connectors">
						<button
							type="button"
							className="mc-side-nav-item"
							aria-current={showConnectors ? "page" : undefined}
							aria-label="Connectors"
							onClick={() => openSurface("connectors")}
						>
							<CableIcon className="size-4 shrink-0" />
							<span className="mc-side-text">Connectors</span>
						</button>
					</SideTip>

					<SideTip label="Channels">
						<button
							type="button"
							className="mc-side-nav-item"
							aria-current={showChannels ? "page" : undefined}
							aria-label="Channels"
							onClick={() => openSurface("channels")}
						>
							<MessagesSquareIcon className="size-4 shrink-0" />
							<span className="mc-side-text">Channels</span>
						</button>
					</SideTip>
						</>
					)}
				</div>

				{sidebarOpen || narrow ? (
					<div className="mc-sidebar-scroll">
						<p className="mc-side-label">
							{locked ? "This chat" : "Recents"}
						</p>
						<ul className="mc-recents">
							{sortedRecents.map((thread) => (
								<RecentThreadRow
									key={thread.id}
									thread={thread}
									active={!showCatalog && thread.id === active.id}
									busy={chatHasBackgroundWork(thread.messages)}
									onSelect={() => handleSelectThread(thread.id)}
									onPin={() => onPinThread(thread.id)}
									onRename={(title) => onRenameThread(thread.id, title)}
									onDelete={() =>
										setPendingDelete({
											id: thread.id,
											title: thread.title,
										})
									}
								/>
							))}
						</ul>
					</div>
				) : (
					<SideTip label="Open sidebar">
						<button
							type="button"
							className="mc-sidebar-expand-hit"
							aria-label="Open sidebar"
							onClick={() => setSidebarOpen(true)}
						/>
					</SideTip>
				)}

				<div className="mc-sidebar-foot">
					<div className="mc-sidebar-foot-row">
						<DropdownMenu>
							<SideTip label={userName || "Account"} side="top">
								<DropdownMenuTrigger asChild>
									<button
										type="button"
										className="mc-profile"
										aria-label="Account"
									>
										{userImageUrl ? (
											<img
												className="mc-profile-avatar mc-profile-avatar--img"
												src={userImageUrl}
												alt=""
												width={28}
												height={28}
											/>
										) : (
											<span className="mc-profile-avatar" aria-hidden>
												{profileInitial}
											</span>
										)}
										<span className="mc-profile-name mc-side-text">
											{userName || "You"}
											<PlanBadge />
										</span>
									</button>
								</DropdownMenuTrigger>
							</SideTip>
							<DropdownMenuContent align="start" side="top" className="w-52">
								<DropdownMenuLabel>Account</DropdownMenuLabel>
								<DropdownMenuItem onClick={onOpenSettings}>
									<SettingsIcon className="size-4" />
									Settings
								</DropdownMenuItem>
								<DropdownMenuItem onClick={onOpenBilling}>
									<CreditCardIcon className="size-4" />
									Billing
								</DropdownMenuItem>
								<DropdownMenuItem onClick={onOpenDesk}>
									<LayoutGridIcon className="size-4" />
									Open Desk
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DevResetTrigger variant="menu" onClick={openReset} />
								<DropdownMenuItem onClick={onSignOut}>
									<LogOutIcon className="size-4" />
									Sign out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<SideTip
							label={`Theme: ${themePref}`}
							side={sidebarOpen ? "top" : "right"}
						>
							<Button
								type="button"
								variant="ghost"
								size="icon-sm"
								className="mc-theme-toggle shrink-0"
								aria-label={`Theme: ${themePref}. Click to change.`}
								onClick={onCycleTheme}
							>
								<ThemeToggleIcon preference={themePref} />
							</Button>
						</SideTip>
					</div>
				</div>
			</aside>

			<DeleteChatConfirm
				open={pendingDelete !== null}
				title={pendingDelete?.title ?? ""}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null);
				}}
				onConfirm={() => {
					if (pendingDelete) onDeleteThread(pendingDelete.id);
				}}
			/>

			<main className={cn("mc-main", sitePreviewUrl && "mc-main--split")}>
				{narrow && sidebarOpen ? (
					<button
						type="button"
						className="mc-sidebar-scrim"
						aria-label="Close sidebar"
						onClick={() => setSidebarOpen(false)}
					/>
				) : null}
				{narrow && !sidebarOpen ? (
					<div
						className="mc-sidebar-edge"
						onPointerDown={(event) => {
							const startX = event.clientX;
							const startY = event.clientY;
							const onMove = (move: PointerEvent) => {
								if (
									move.clientX - startX > 40 &&
									Math.abs(move.clientY - startY) < 48
								) {
									setSidebarOpen(true);
									cleanup();
								}
							};
							const cleanup = () => {
								window.removeEventListener("pointermove", onMove);
								window.removeEventListener("pointerup", cleanup);
							};
							window.addEventListener("pointermove", onMove);
							window.addEventListener("pointerup", cleanup);
						}}
					/>
				) : null}
				<div className="mc-main-chat">
				<header
					className={cn(
						"mc-topbar",
						narrow && "mc-topbar--app",
						(showHome || showOnboardingHome) && "mc-topbar--home",
						locked && "mc-topbar--onboarding",
					)}
				>
					{narrow || !sidebarOpen ? (
						<Button
							type="button"
							variant="ghost"
							size="icon-sm"
							className="mc-topbar-reopen"
							aria-label={sidebarOpen ? "Close sidebar" : "Show sidebar"}
							aria-expanded={sidebarOpen}
							onClick={() => setSidebarOpen((open) => !open)}
						>
							<PanelLeftIcon className="size-4" />
						</Button>
					) : (
						<span className="mc-topbar-spacer" aria-hidden />
					)}
					{narrow && (showHome || showOnboardingHome) ? (
						<div className="mc-topbar-mark" aria-hidden={false}>
							<img
								className="brand-logo brand-logo--dark"
								src="/brand/mairko-logo-primary.svg"
								alt="Mairko"
								height={22}
							/>
							<img
								className="brand-logo brand-logo--light"
								src="/brand/mairko-logo-onlight.svg"
								alt="Mairko"
								height={22}
							/>
						</div>
					) : showCatalog ? (
						<p className="mc-topbar-title truncate">{catalogTitle}</p>
					) : !fresh && !showOnboardingHome ? (
						<p className="mc-topbar-title truncate">{active.title}</p>
					) : (
						<span className="mc-topbar-title" aria-hidden />
					)}
					<div className="mc-ob-actions">
						{narrow ? null : (
							<>
								<DevModelSelect value={modelId} onChange={setModelId} />
								<DevResetTrigger variant="bar" onClick={openReset} />
							</>
						)}
						{locked ? (
							narrow ? (
								<span className="mc-topbar-spacer" aria-hidden />
							) : (
								<button
									type="button"
									className="mc-ob-signout"
									onClick={onSignOut}
								>
									<LogOutIcon className="size-3.5" aria-hidden />
									Sign out
								</button>
							)
						) : (
							<Button
								type="button"
								variant="ghost"
								size="icon-sm"
								aria-label="New chat"
								onClick={handleNewChat}
							>
								<SquarePenIcon className="size-4" />
							</Button>
						)}
					</div>
				</header>

				{showAgents ? (
					<div className="mc-connectors-pane main">
						<AgentsHub setupDone={setupDone} onOpen={onOpenAgent} />
					</div>
				) : showConnectors ? (
					<div className="mc-connectors-pane main">
						{selectedIntegration ? (
							<IntegrationDetail
								item={selectedIntegration}
								kind="connector"
								onBack={onClearIntegration}
								platformRow={platformRowFor(selectedIntegration.id)}
								onConnectionChange={onConnectionChange}
								oauthFlash={oauthFlash}
								oauthFlashTone={oauthFlashTone}
							/>
						) : (
							<ConnectorsHub
								view={connectorsView}
								filter={connectorsFilter}
								onViewChange={onConnectorsViewChange}
								onFilterChange={onConnectorsFilterChange}
								onOpen={(item) => onSelectIntegration(item, "connector")}
								items={connectors}
							/>
						)}
					</div>
				) : showChannels ? (
					<div className="mc-connectors-pane main">
						{selectedIntegration ? (
							<IntegrationDetail
								item={selectedIntegration}
								kind="channel"
								onBack={onClearIntegration}
								platformRow={platformRowFor(selectedIntegration.id)}
								onConnectionChange={onConnectionChange}
								oauthFlash={oauthFlash}
								oauthFlashTone={oauthFlashTone}
							/>
						) : (
							<ChannelsHub
								view={channelsView}
								filter={channelsFilter}
								onViewChange={onChannelsViewChange}
								onFilterChange={onChannelsFilterChange}
								onOpen={(item) => onSelectIntegration(item, "channel")}
								items={channels}
							/>
						)}
					</div>
				) : showSites ? (
					<div className="mc-connectors-pane main">
						<SitesHub
							onOpenPreview={(url) => {
								onOpenSitePreview?.(url);
							}}
							onPublish={(siteId, siteName) => {
								onSurfaceChange("messages");
								onPublishSite?.(siteId, siteName);
							}}
						/>
					</div>
				) : showOnboardingHome ? (
					<div className="mc-home">
						<div className="mc-home-center">
							<OnboardingIntro firstName={firstName} />
							{composer("home")}
						</div>
					</div>
				) : showHome ? (
					<div className="mc-home">
						<div className="mc-home-center">
							<div className="mc-home-hero">
								<MairkoMark size="home" shimmer="off" />
								<h1 className="mc-ob-hello">
									{firstName?.trim()
										? `Hey, ${firstName.trim()}.`
										: "Hey."}
								</h1>
							</div>
							{composer("home")}
							<HomeStarters
								items={DESK_STARTERS}
								disabled={typing}
								onPick={(text) => onSubmit(text)}
							/>
						</div>
					</div>
				) : (
					<WebsiteDeskStrip
						previewUrl={openHostedSite ? sitePreviewUrl : null}
						host={websiteDeskHost}
						onPublish={onPublishSite}
					>
						<Conversation className="mc-thread">
							<ConversationContent className="mc-thread-content">
								{visibleMessages.map((message) => (
									<div key={message.id} className="mc-msg">
										<Message
											message={message}
											variant="chat"
											busy={typing}
											onEdit={onEditMessage}
											onRetry={onRetryMessage}
											onOpenAgent={onOpenAgent}
											onOpenSitePreview={onOpenSitePreview}
											onHandoffPatch={onHandoffPatch}
											onHandoffDecision={onHandoffDecision}
											onPublishSite={onPublishSite}
											onPublishDecision={onPublishDecision}
											onSiteCloneCard={onSiteCloneCard}
											onSiteDesignPatch={onSiteDesignPatch}
											onCompanyResearchPatch={onCompanyResearchPatch}
											onConfirmCompanyPack={onConfirmCompanyPack}
											confirmingPackId={confirmingPackId}
											onWorkingCopyYes={(ask: ChatWorkingCopyAsk, messageId: string) => {
												onWorkingCopyYes?.(ask, messageId);
											}}
											onWorkingCopyLater={onWorkingCopyLater}
											workingCopyPaying={
												workingCopyCheckout?.messageId === message.id
											}
											onWorkingCopyPaid={onWorkingCopyPaid}
											onCardAction={onCardAction}
										/>
									</div>
								))}
							</ConversationContent>
							<ConversationScrollButton />
						</Conversation>
						<div className="mc-composer-dock">{composer("dock")}</div>
					</WebsiteDeskStrip>
				)}
				</div>
				{sitePreviewUrl && onCloseSitePreview ? (
					<SitePreview
						url={sitePreviewUrl}
						title={previewTitle}
						onClose={onCloseSitePreview}
						onPublish={onPublishSite}
					/>
				) : null}
			</main>
			{resetDialog}
		</div>
	);
}
